
import React, { useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useEnhancedAuth } from '@/hooks/useEnhancedAuth';
import { supabase } from '@/integrations/supabase/client';
import { initializeSecurityHeaders, preventClickjacking } from '@/utils/securityUtils';

interface SecurityWrapperProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const SecurityWrapper: React.FC<SecurityWrapperProps> = ({ children, requireAdmin = false }) => {
  const { user } = useAuth();
  const { logSecurityEvent } = useEnhancedAuth();

  useEffect(() => {
    // Initialize comprehensive security headers
    initializeSecurityHeaders();
    
    // Prevent clickjacking attacks
    try {
      preventClickjacking();
    } catch (error) {
      // Only log serious security violations, not development environment warnings
      if (!window.location.hostname.includes('lovableproject.com')) {
        console.error('Security violation detected:', error);
        logSecurityEvent('CLICKJACKING_ATTEMPT', { error: error.message });
      }
    }
  }, [logSecurityEvent]);

  useEffect(() => {
    // Enhanced session validation with security logging
    const validateSession = async () => {
      if (!user) return;

      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          await logSecurityEvent('SESSION_VALIDATION_FAILED', { error });
          await supabase.auth.signOut();
          return;
        }

        // Check session expiration with buffer
        const now = Date.now();
        const sessionExpiry = session.expires_at ? session.expires_at * 1000 : 0;
        const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

        if (sessionExpiry && now >= (sessionExpiry - bufferTime)) {
          await logSecurityEvent('SESSION_EXPIRED');
          await supabase.auth.signOut();
          return;
        }

        // Additional admin validation if required
        if (requireAdmin) {
          const { data: adminData, error: adminError } = await supabase
            .from('admin_users')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .single();

          if (adminError || !adminData) {
            await logSecurityEvent('ADMIN_ACCESS_DENIED', { adminError });
            await supabase.auth.signOut();
            return;
          }
        }

      } catch (err) {
        console.error('Session validation error:', err);
        await logSecurityEvent('SESSION_VALIDATION_ERROR', { error: err.message });
        await supabase.auth.signOut();
      }
    };

    // Validate session immediately and then periodically
    validateSession();
    const interval = setInterval(validateSession, 3 * 60 * 1000); // Every 3 minutes

    return () => clearInterval(interval);
  }, [user, requireAdmin, logSecurityEvent]);

  return <>{children}</>;
};

export default SecurityWrapper;
